
import React, { useEffect, useState } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { Navigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { OwnerDashboardHeader } from "@/components/nursing-homes/owner/OwnerDashboardHeader";
import { ClaimedNursingHomes } from "@/components/nursing-homes/owner/ClaimedNursingHomes";
import { toast } from "sonner";

export default function OwnerDashboardPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [claimedHomes, setClaimedHomes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchClaimedNursingHomes = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        
        // First, get all approved claims for this user
        const { data: claims, error: claimsError } = await supabase
          .from('nursing_home_claims')
          .select('nursing_home_id')
          .eq('user_id', user.id)
          .eq('status', 'approved');
        
        if (claimsError) throw claimsError;
        
        if (claims && claims.length > 0) {
          // Get the nursing home details for all claimed homes
          const nursingHomeIds = claims.map(claim => claim.nursing_home_id);
          
          const { data: homes, error: homesError } = await supabase
            .from('nursing_homes')
            .select('*')
            .in('nh_slug', nursingHomeIds);
          
          if (homesError) throw homesError;
          
          setClaimedHomes(homes || []);
        } else {
          setClaimedHomes([]);
        }
      } catch (err) {
        console.error("Error fetching claimed nursing homes:", err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        toast.error("Failed to load your nursing homes");
      } finally {
        setIsLoading(false);
      }
    };

    if (user && !authLoading) {
      fetchClaimedNursingHomes();
    }
  }, [user, authLoading]);

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth/login?redirectTo=/owner/dashboard" />;
  }

  return (
    <Layout>
      <Helmet>
        <title>Owner Dashboard | Grandpal</title>
      </Helmet>

      <div className="container py-4 md:py-8 max-w-7xl">
        <OwnerDashboardHeader user={user} />
        
        <div className="grid grid-cols-1 gap-4 md:gap-6">
          <ClaimedNursingHomes 
            homes={claimedHomes} 
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>
    </Layout>
  );
}
